import { FormGroup, Button, Col, Input, Modal, ModalBody, ModalFooter, ModalHeader, Row, UncontrolledTooltip, InputGroup, InputGroupText, Label } from "reactstrap";
import { useTheme } from "../../../hooks/useTheme";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import DataTableWindowed from "../../../components/DataTableWindowed";
import { useQuery } from "@tanstack/react-query";
import { fetchActiveItemLookupVersionByItemLookupId } from "../../items/data/itemLookupVersionService";
import CustomSelect from "../../../components/CustomSelect";
import CustomCreatableSelect from "../../../components/CustomCreatableSelect";
import ItemsTable from "../../items/components/ItemsTable";
// NEW: lookups for suppliers and item types
import { fetchSuppliers } from "../../suppliers/data/supplierService";
import { fetchItemLookupType } from "../../items/data/itemLookupTypeService";
import { formatCurrency } from "../../../utils/numberUtils";
import { getBlankQuoteItem, sanitizeForId } from "../../quotes/functions/quoteFunctions";
import QuoteItemBulkModal from "../../quotes/components/quoteItemBulkModal";
import {
    mapV1LicenseIdToCatalogueId,
    mapV1UnitCostIdToCatalogueId,
} from "../constants/v1CatalogueMap";
import { 
    V1_QUOTE_ONLY_LICENCES, 
    V1_QUOTE_ONLY_UNITCOSTS, 
    V1_INFRASTRUCTURE_UNITCOSTS, 
    V1_PS_UNITCOSTS 
} from "../constants/iQuoteV1Constants";

// Sentinel value used when the user explicitly chooses to create a new item
const NEW_ITEM_SENTINEL = "__NEW__";

// Default page size for data table pagination
const DEFAULT_TABLE_PAGE_SIZE = 100000;

// Core (non-attribute) field keys in camelCase
const CORE_FIELDS = new Set([
    "internalName", "nameOnQuote", "itemType", "supplier", "autotaskCode", "upfrontSalesforceCode", "recurringSalesforceCode", "quantity",  "designPackRevisionId",
    "salesforceCode",
    "billingType", "upfrontCost", "upfrontPrice", "upfrontNameOnQuote", "recurringCost", "recurringPrice", "recurringNameOnQuote",
    "recurringStartMonth", "recurringIntervalMonths", "recurringNumPayments",
    "tags", "id", "options", "isInternal", "groupName", "disabled"
]);

// Curated labels for known core fields
const CORE_LABELS = {
    internalName: "Internal Name",
    nameOnQuote: "Name On Quote",
    itemType: "Item type",
    upfrontSalesforceCode: "Upfront Salesforce code",
    recurringSalesforceCode: "Recurring Salesforce code",
    autotaskCode: "Autotask code",
    upfrontCost: "Upfront cost",
    upfrontPrice: "Upfront price",
    recurringCost: "Recurring cost",
    recurringPrice: "Recurring price",
    quantity: "Qty",
    supplier: "Supplier",
    groupName: "Group Name",
    itemId: "ItemId",
    salesforceCode: "Salesforce code",
    disabled: "Disabled?"
};

// Pretty-print a camelCase key as a fallback
const prettyFromKey = (k = "") =>
    k.replace(/([A-Z])/g, " $1").replace(/\s+/g, " ").trim()
        .replace(/^./, (c) => c.toUpperCase());

const LegacyQuotesImportModal = ({ toggle, items, addQuoteItems, title, fullCatalogue }) => {
    const theme = useTheme();
    useEffect(() => {
        const normalizedItems = Array.isArray(items) ? items : (items ? [items] : []);

        const itemsWithQuoteOnlyCostsRemoved = normalizedItems.map((it) => {
            const itemId = String(it?.itemId ?? "");
            const [prefixRaw = "", idPart = ""] = itemId.split(".");
            const prefix = prefixRaw.toLowerCase();
            const numericId = Number(idPart);

            const shouldRemove = (prefix === "licenses" && V1_QUOTE_ONLY_LICENCES.includes(numericId)) ||
                (prefix === "unitcosts" && V1_QUOTE_ONLY_UNITCOSTS.includes(numericId));

            console.log("Should REmove", shouldRemove, itemId, prefix, numericId);

            return {
                ...it,
                disabled: it?.disabled ?? !!it?.paused,
                upfrontCost: shouldRemove ? 0 : it.upfrontCost,
                recurringCost: shouldRemove ? 0 : it.recurringCost,
            }
        });

        const itemsMovedIntoGroups = itemsWithQuoteOnlyCostsRemoved.map((item) => {
            const itemId = String(item?.itemId ?? "");
            const [prefixRaw = "", idPart = ""] = itemId.split(".");
            const prefix = prefixRaw.toLowerCase();
            const numericId = Number(idPart);
            if (prefix === "unitcosts" && V1_INFRASTRUCTURE_UNITCOSTS.includes(numericId)) {
                return {
                    ...item,
                    groupName: "Maintel Infrastructure",
                };
            } else if (prefix === "unitcosts" && V1_PS_UNITCOSTS.includes(numericId)) {
                return {
                    ...item,
                    groupName: "Professional Services",
                };
            }
            return item;
        });

        console.log("normalised items:", normalizedItems);
        console.log("filtered items:", itemsWithQuoteOnlyCostsRemoved);
        console.log("items moved into groups:", itemsMovedIntoGroups);

        setRows(itemsMovedIntoGroups);
    }, [items]);
    

    const [rows, setRows] = useState([]);                 // raw import rows
    
    // Keep all mapped rows indexed by __rowId as user navigates pages
    const allMappedRowsRef = useRef(new Map());

    // catalogue search modal
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchRow, setSearchRow] = useState(null);

    // bulk edit modal state
    const [bulkModalOpen, setBulkModalOpen] = useState(false);
    const [bulkItems, setBulkItems] = useState([]);

    // array of group names
    const [groupNames, setGroupNames] = useState([]);

    const [selectedIds, setSelectedIds] = useState(new Set()); // IDs of rows selected via checkboxes, used for bulk edit modal

    const openBulkWithItems = useCallback((items) => {
        setBulkItems(items || []);
        setBulkModalOpen(true);
    }, []);

    const openSearch = useCallback((row) => {
        setSearchRow(row);
        setIsSearchOpen(true);
    }, []);

    const closeSearch = useCallback(() => {
        setIsSearchOpen(false);
        setSearchRow(null);
    }, []);

    // mapped data (catalogue base + import overlay)
    const [mappedData, setMappedData] = useState([]);
    const selectedData = useMemo(() => mappedData.filter(r => selectedIds.has(r.__rowId)), [mappedData, selectedIds]);

    // Local query state for DataTablePageQuery
    const [tablePageNumber, setTablePageNumber] = useState(1);
    const [tablePageSize, setTablePageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
    const [tableSearchTerm, setTableSearchTerm] = useState("");
    const [tableSortOptions, setTableSortOptions] = useState({ key: "__rowId", direction: "ascending" });

    const buildDynamicColumns = (preview) => {
        if (!Array.isArray(preview) || preview.length === 0) return [];

        const skipKeys = new Set([
            // internal/meta
            "_importRaw", "_linkItem", "_linkMatchIds", "_linkCandidateIds",
            "_linkSelectedId", "_linkLabel", "_linkStatus", "_linkMatchesCount",
            "__rowId",
            // we render these in dedicated columns
            "typeCustomAttributes", "customAttributes",
            // hide boolean flags (use billingType instead)
            "isRecurring", "isUpfront", "revisionNumber", "designPackRevisionId", "itemLookupTypeId",
            "groupName", "categoryName", "recurringNumPayments", "recurringIntervalMonths", "recurringStartMonth", "isInternal", "isCreditItem", "upfrontNameOnQuote", "recurringNameOnQuote",
            "disabled"
        ]);

        // Prefer to show these first (and in this order) if present
        const preferred = [
            "groupName",
            "catalogueLink",
            "internalName",
            "nameOnQuote",
            "itemType",
            "supplier",
            "autotaskCode",
            "salesforceCode",
            "quantity",
            "billingType",
            "upfrontCost",
            "upfrontPrice",
            "recurringCost",
            "recurringPrice",
            "recurringStartMonth",
            "recurringIntervalMonths",
            "recurringNumPayments",
            "isInternal"
        ];

        // Gather keys that actually exist with meaningful values
        const exists = new Set();
        const presentKeys = new Set();
        for (const r of preview) {
            Object.entries(r || {}).forEach(([k, v]) => {
                if (!k || k.startsWith("_")) return;

                presentKeys.add(k);

                // Normalize key to camelCase for skipKeys check
                const normalizedKey = toCamelKey(k);
                if (skipKeys.has(normalizedKey)) return;
                
                // Show if non-empty OR explicitly false/0
                const nonEmpty = v !== null && v !== undefined && v !== "";
                if (nonEmpty || v === 0 || v === false) {
                    exists.add(k);
                }
            });
        }

        // Order: preferred first (that exist), then the rest
        const ordered = [
            ...preferred.filter((k) => exists.has(k) || 
                (k === "nameOnQuote" && presentKeys.has(k))
            ),
            ...[...exists].filter((k) => !preferred.includes(k)),
        ];

        const dedupedOrdered = Array.from(new Map(ordered.map(k => [k.toLowerCase(), k])).values());
        return dedupedOrdered.map(key => ({
            key,
            label: labelForKey(key),
            render: (value) => {
                if (value === null || value === undefined) return "";
                if (typeof value === "object") return JSON.stringify(value);
                if (typeof value === "boolean") return value ? "Yes" : "No";
                return key === "itemId" ? <Label style={{ maxWidth: "110px", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</Label> : String(value);
            }
        }));
    };

    // Reset selections when the dataset is rebuilt (e.g., new upload)
    useEffect(() => {
        setSelectedIds(new Set());
    }, [rows]);

    useEffect(() => {
        setTablePageNumber(1);
        setTablePageSize(DEFAULT_TABLE_PAGE_SIZE);
        setTableSearchTerm("");
        setTableSortOptions({ key: "__rowId", direction: "ascending" });
    }, [rows]);


    // --- Existing Catalogue (fetch all pages once and cache) ---
    const [catalogueCache, setCatalogueCache] = useState(() => new Map());


    const normalizeMatchValue = (v) => String(v ?? "").trim().toLowerCase();

    const readItemField = (item, fieldKey) => {
        if (!item) return "";
        if (fieldKey === "internalName") {
            return item?.name ?? item?.internalName ?? "";
        }
        return item?.[fieldKey] ?? "";
    };


    // NEW: fetch suppliers and item types (for names + dynamic attributes)
    const { data: suppliersData = [] } = useQuery({
        queryKey: ["importSuppliers"],
        queryFn: fetchSuppliers,
        staleTime: 60_000,
    });

    const { data: itemTypes = [] } = useQuery({
        queryKey: ["importItemLookupTypes"],
        queryFn: fetchItemLookupType,
        staleTime: 60_000,
    });

    // --- Lookups & helpers ------------------------------------------------------

    // Keep only one key per case-insensitive name, preferring the canonical form when we know it
    const DEDUPE_CANON = new Map(
        Array.from(CORE_FIELDS).map(k => [String(k).toLowerCase(), k])
    );

    function dedupeCaseKeys(obj) {
        const out = {};
        const seen = new Set(); // lowercased keys we've already emitted

        for (const [k, v] of Object.entries(obj || {})) {
            const lower = String(k).toLowerCase();
            if (seen.has(lower)) continue;

            // if we know a canonical key for this, use it; else keep the first we saw
            const canon = DEDUPE_CANON.get(lower) || k;
            out[canon] = v;
            seen.add(lower);
        }
        return out;
    }

    // Merge strategy: prefer existing non-empty values, else last wins
    const isEmptyVal = (v) =>
        v == null || (typeof v === "string" && v.trim() === "");

    const normalizeRowKeys = (row = {}) => {
        const out = {};
        for (const [rawKey, val] of Object.entries(row || {})) {
            // 1) turn “weird headers” into camel-ish
            const camel = toCamelKey(rawKey);
            // 2) then force to your canonical key name if we know it
            const canon = toCanonicalKey(camel);

            // remember how the user named the column for pretty labels
            registerLabel(canon, rawKey);

            if (!(canon in out) || isEmptyVal(out[canon])) {
                out[canon] = val;
            }
        }
        return out;
    };

    const idToItem = useMemo(() => {
        const m = new Map();
        for (const it of Array.isArray(fullCatalogue) ? fullCatalogue : []) {
            if (it?.id != null) m.set(String(it.id), it);
        }
        for (const [id, item] of catalogueCache.entries()) {
            m.set(String(id), item);
        }
        return m;
    }, [fullCatalogue, catalogueCache]);

    const supplierIdToName = useMemo(() => {
        const m = new Map();
        (suppliersData || []).forEach((s) => m.set(String(s.id), s.supplierName || s.name || `#${s.id}`));
        return m;
    }, [suppliersData]);

    const supplierNameToId = useMemo(() => {
        const m = new Map();
        (suppliersData || []).filter(x => !x.disabled).forEach((s) => {
            const name = String(s.supplierName || s.name || "").trim().toLowerCase();
            if (name) m.set(name, s.id);
        });
        return m;
    }, [suppliersData]);

    const typeIdToName = useMemo(() => {
        const m = new Map();
        (itemTypes || []).forEach((t) => m.set(String(t.id), t.itemLookupTypeName || t.name || `#${t.id}`));
        return m;
    }, [itemTypes]);

    const typeNameToDef = useMemo(() => {
        const m = new Map();
        (itemTypes || []).forEach((t) =>
            m.set(String(t.itemLookupTypeName || t.name || "").trim().toLowerCase(), t)
        );
        return m;
    }, [itemTypes]);

    // util
    const toCamelKey = (s = "") =>
        s
            .replace(/[^a-zA-Z0-9 ]+/g, " ")
            .trim()
            .split(/\s+/)
            .map((w, i) => {
                if (i === 0) {
                    // if all caps (e.g. "DESCRIPTION"), lowercase it
                    if (w.toUpperCase() === w) return w.toLowerCase();
                    // otherwise just lowercase first char (e.g. "TypeCustomAttributes" -> "typeCustomAttributes")
                    return w.charAt(0).toLowerCase() + w.slice(1);
                }
                return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
            })
            .join("");

    // !!! HOISTED: normalizeAttrCollection used below
    function normalizeAttrCollection(input) {
        if (input == null || input === "") return [];
        let arr = input;
        if (typeof arr === "string") {
            try { arr = JSON.parse(arr); } catch { return []; }
        }
        if (!Array.isArray(arr)) return [];
        return arr
            .map((x) => ({
                label: String(x?.label ?? x?.name ?? x?.key ?? "").trim(),
                value: x?.value ?? null,
            }))
            .filter((x) => x.label);
    }

    // Collect attribute labels from ITEM TYPES
    const typeAttrLabelMap_fromTypes = useMemo(() => {
        const m = new Map();
        (itemTypes || []).forEach((t) =>
            (t?.typeCustomAttributes || []).forEach((a) => {
                const key = toCamelKey(a?.label ?? a?.name ?? a?.key ?? "");
                const label = String(a?.label ?? a?.name ?? a?.key ?? "").trim();
                if (key) m.set(key, label);
            })
        );
        return m;
    }, [itemTypes]);

    // FINAL: union of (types ∪ catalogue ∪ template)
    const typeAttrLabelMap = useMemo(() => {
        const merged = new Map();
        for (const src of [typeAttrLabelMap_fromTypes]) {
            for (const [k, v] of src.entries()) if (!merged.has(k)) merged.set(k, v);
        }
        return merged;
    }, [typeAttrLabelMap_fromTypes]);

    const typeAttrLabelKeys = useMemo(() => new Set(typeAttrLabelMap.keys()), [typeAttrLabelMap]);

    // Build a lowercase->canonical map for known keys
    const CANON_KEY_MAP = useMemo(() => {
        const toCanon = new Map();

        // Start with your curated/core fields
        CORE_FIELDS.forEach((k) => {
            toCanon.set(String(k).toLowerCase(), k);
        });

        // canonicalize any attribute labels that become keys
        typeAttrLabelMap.forEach((_, camelKey) => {
            toCanon.set(String(camelKey).toLowerCase(), camelKey);
        });

        toCanon.set("type", "itemType"); // special case

        return toCanon;
    }, [typeAttrLabelMap]);

    const toCanonicalKey = useCallback((k) => CANON_KEY_MAP.get(String(k || "").toLowerCase()) || k, [CANON_KEY_MAP]);


    // Pick the version with the greatest activeStartDate <= now; tie-break by createdAt
    const getCurrentVersion = (versions, now = new Date()) => {
        if (!Array.isArray(versions) || versions.length === 0) return null;
        const nowTime = +now;
        let best = null;
        for (const v of versions) {
            const start = new Date(v.activeStartDate).getTime();
            if (Number.isNaN(start) || start > nowTime) continue;
            if (!best) { best = v; continue; }
            const bestStart = new Date(best.activeStartDate).getTime();
            if (start > bestStart) best = v;
            else if (start === bestStart) {
                const created = new Date(v.createdAt).getTime() || 0;
                const bestCreated = new Date(best.createdAt).getTime() || 0;
                if (created > bestCreated) best = v;
            }
        }
        return best;
    };

    const labelForItem = (item) => {
        if (!item) return "";
        const base =
            item.upfrontNameOnQuote ||
            item.recurringNameOnQuote ||
            item.name ||
            item.autotaskCode;
        return item.options ? `${base}, ${item.options}` : base;
    };

    const coerceBooleans = (v) => {
        if (typeof v === "string") v = v.trim().toLowerCase();
        return v === true || v === "true" || v === "yes" || v === "y" || v === "1" || v === 1;
    };

    const getRowBillingFlagsForLink = (row) => {
        const raw = row?._importRaw ?? {};

        return {
            isUpfront: coerceBooleans(row?.isUpfront ?? raw?.isUpfront),
            isRecurring: coerceBooleans(row?.isRecurring ?? raw?.isRecurring),
        };
    };

    const billingTypesMatchForSalesforce = (row, version) => {
        const rowFlags = getRowBillingFlagsForLink(row);
        const versionFlags = {
            isUpfront: coerceBooleans(version?.isUpfront),
            isRecurring: coerceBooleans(version?.isRecurring),
        };

        return rowFlags.isUpfront === versionFlags.isUpfront && rowFlags.isRecurring === versionFlags.isRecurring;
    };

    const getCatalogueItemBillingFlagsForLink = (item) => {
        const currentVersion = getCurrentVersion(item?.itemLookupVersions || []);
        return {
            isUpfront: coerceBooleans(currentVersion?.isUpfront ?? item?.isUpfront),
            isRecurring: coerceBooleans(currentVersion?.isRecurring ?? item?.isRecurring),
        };
    };

    const billingTypesMatchForLink = (row, item) => {
        const rowFlags = getRowBillingFlagsForLink(row);
        const itemFlags = getCatalogueItemBillingFlagsForLink(item);

        return rowFlags.isUpfront === itemFlags.isUpfront && rowFlags.isRecurring === itemFlags.isRecurring;
    };

    const deriveBillingType = (row) => {
        const up = !!row.isUpfront;
        const rec = !!row.isRecurring;
        return billingTypeFromFlags(up, rec);
    };

    // Which fields are link keys
    const linkFieldKeys = useMemo(() => {
        const linkKeys = ["internalName", "autotaskCode"];
        return linkKeys;
    }, [items, toCanonicalKey]);

    const catalogueIndex = useMemo(() => {
        const idx = {};
        for (const k of linkFieldKeys) idx[k] = new Map();

        for (const item of idToItem.values()) {
            for (const k of linkFieldKeys) {
                const raw = readItemField(item, k);
                const v = normalizeMatchValue(raw);
                if (!v) continue;
                const bucket = idx[k].get(v);
                if (bucket) bucket.push(item);
                else idx[k].set(v, [item]);
            }
        }

        return idx;
    }, [idToItem, linkFieldKeys]);

    const linkRowExact = useCallback((row) => {
        let matchingKey = '';

        const rawV1ItemId =
            row?._importRaw?.itemId ??
            row?.itemId ??
            "";
        const normalizedRawItemId = String(rawV1ItemId).trim();
        const isLicenseItemId = /^licenses?\./i.test(normalizedRawItemId);
        const isUnitCostItemId = /^unitcosts?\./i.test(normalizedRawItemId);
        const [, v1IdPart = ""] = normalizedRawItemId.split(".");
        if (isLicenseItemId || isUnitCostItemId) {
            const v1IncomingId = Number(v1IdPart);
            if (Number.isFinite(v1IncomingId)) {
                const mappedCatalogueId = isLicenseItemId
                    ? mapV1LicenseIdToCatalogueId(v1IncomingId)
                    : mapV1UnitCostIdToCatalogueId(v1IncomingId);
                if (mappedCatalogueId != null) {
                    const mappedItem = idToItem.get(String(mappedCatalogueId));
                    if (mappedItem && billingTypesMatchForLink(row, mappedItem)) {
                        return {
                            status: "OK",
                            id: mappedItem.id,
                            item: mappedItem,
                            label: labelForItem(mappedItem),
                            key: isLicenseItemId ? "itemIdLicenseMapping" : "itemIdUnitCostMapping",
                        };
                    }
                }
            }
        }

        const activeKeys = linkFieldKeys.filter((k) => {            
            const rv = row?.[k];
            return rv != null && String(rv).trim() !== "";
        });

        console.log('[linkRowExact] activeKeys:', activeKeys, 'row:', row);

        if (activeKeys.length === 0) return { status: "NO_ROW_VALUES" };

        let candidatesMap = null;
        for (const k of activeKeys) {
            const v = normalizeMatchValue(row[k]);
            const arr = catalogueIndex[k]?.get(v) ?? [];
            const billingMatchedArr = arr.filter((it) => billingTypesMatchForLink(row, it));
            //console.log(`[linkRowExact] Checking key "${k}" with value "${v}":`, arr.length, 'matches');
            
            // Priority Strategy:
            // 1. If we find a UNIQUE match (count=1), use it and stop immediately.
            // 2. If we find AMBIGUOUS matches (count>1), store them as fallback but continue ensuring no better unique match exists on lower-priority keys.
            if (billingMatchedArr.length === 1) {
                candidatesMap = new Map(billingMatchedArr.map((it) => [it.id, it]));
                console.log(`[linkRowExact] Found unique match on "${k}". Link established.`);
                matchingKey = k;
                break;
            }

            if (billingMatchedArr.length > 1 && candidatesMap === null) {
                candidatesMap = new Map(billingMatchedArr.map((it) => [it.id, it]));
                console.log(`[linkRowExact] Found ambiguous matches (${billingMatchedArr.length}) on "${k}". Storing as fallback.`);
                matchingKey = k;
            }
        }

        const matches = candidatesMap ? Array.from(candidatesMap.values()) : [];
        if (matches.length === 1)
            return { status: "OK", id: matches[0].id, item: matches[0], label: labelForItem(matches[0]), key: matchingKey };
        if (matches.length > 1) {
            const candidates = matches.map((m) => ({
                value: String(m.id),
                label: m.upfrontNameOnQuote || m.recurringNameOnQuote || m.name || `#${m.id}`,
                item: m,
            }));
            return {
                status: "CONFLICT",
                count: matches.length,
                ids: matches.map((m) => m.id),
                candidates,
                key: matchingKey,
            };
        }
        return { status: "NOT_FOUND" };
    }, [catalogueIndex, linkFieldKeys, idToItem, billingTypesMatchForLink]);

    // --- Merge helpers (import wins; catalogue fills blanks) ---
    const isEmpty = (v) =>
        v == null ||
        (typeof v === "string" && v.trim() === "") ||
        (Array.isArray(v) && v.length === 0) ||
        (typeof v === "number" && Number.isNaN(v));

    const toNumOrNull = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    };

    // Billing type label helper (allows both)
    const billingTypeFromFlags = (up, rec) =>
        up && rec ? "Mixed" : up ? "Upfront" : rec ? "Recurring" : "Unassigned";

    const inferBillingFromValues = (raw) => {
        if (coerceBooleans(raw?.isRecurring)) return "Recurring";
        if (coerceBooleans(raw?.isUpfront)) return "Upfront";

        return "Upfront";
    };

    const normAttrLabel = (s) => String(s ?? "").trim();

    const mergeAttrsByLabel = (a = [], b = []) => {
        const map = new Map();
        for (const x of a) {
            const k = normAttrLabel(x.label).toLowerCase();
            if (!k) continue;
            map.set(k, { label: normAttrLabel(x.label), value: x.value ?? null });
        }
        for (const x of b) {
            const k = normAttrLabel(x.label).toLowerCase();
            if (!k) continue;
            map.set(k, { label: normAttrLabel(x.label), value: x.value ?? null });
        }
        return Array.from(map.values());
    };

    const parseAttrColumnsFromRow = (row, baseName) => {
        if (!row) return [];
        const out = [];
        const dot = new RegExp(`^${baseName}\\.([^:.[\\]]+)$`, "i");
        const brk = new RegExp(`^${baseName}\\[([^\\]]+)\\]$`, "i");
        const col = new RegExp(`^${baseName}:([^:.[\\]]+)$`, "i");
        for (const [k, v] of Object.entries(row)) {
            if (v == null || v === "") continue;
            const key = String(k);
            const m = key.match(dot) || key.match(brk) || key.match(col);
            if (m && m[1]) out.push({ label: normAttrLabel(m[1]), value: v });
        }
        return out;
    };

    const slugKey = (s) =>
        String(s).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

    const blankCatalogueForType = (raw) => {
        const tName = String(raw?.itemType || raw?.Type || "").trim().toLowerCase();
        const t = tName ? typeNameToDef.get(tName) : null;
        const typeAttrs = Array.isArray(t?.typeCustomAttributes) ? t.typeCustomAttributes : [];
        const mappedTypeAttrs = typeAttrs
            .map((a) => ({
                label: String(a?.label ?? a?.name ?? a?.key ?? "").trim(),
                value: null,
            }))
            .filter((x) => x.label);
        return {
            id: null,
            internalName: null,
            itemType: t ? (t.itemLookupTypeName || t.name) : (raw?.itemType ?? ""),
            supplier: null,
            groupName: null,
            autotaskCode: null,
            salesforceCode: null,
            quantity: null,
            isInternal: null,
            isUpfront: null,
            isRecurring: null,
            billingType: "Unassigned",
            customAttributes: [],
            typeCustomAttributes: mappedTypeAttrs
        };
    };

    const mergeableKeys = useMemo(() => {
        const extras = [
            "internalName", "itemType", "supplier", "autotaskCode", "salesforceCode", "quantity",
            "billingType", "isUpfront", "isRecurring", "upfrontCost", "upfrontPrice", "upfrontNameOnQuote", "recurringCost", "recurringPrice", "recurringNameOnQuote",
            "recurringStartMonth", "recurringIntervalMonths", "recurringNumPayments", "customAttributes", "typeCustomAttributes", "isInternal", "groupName", "disabled"
        ];        
        
        return Array.from(new Set([...extras]));
    }, [items]);

    const labelRegistryRef = useRef(new Map()); // canonKey -> friendly label
    const registerLabel = (canonKey, rawKey) => {
        if (!canonKey) return;
        if (labelRegistryRef.current.has(canonKey)) return;

        // Prefer curated; else prefer the original header text; else fallback pretty camel
        const rawFriendly = String(rawKey || "").trim();
        const label =
            CORE_LABELS[canonKey] ||
            (rawFriendly ? rawFriendly : prettyFromKey(canonKey));

        labelRegistryRef.current.set(canonKey, label);
    };

    const labelForKey = (key) =>
        CORE_LABELS[key] ||
        labelRegistryRef.current.get(key) ||
        prettyFromKey(key);

/**
 * Applies the overlay rules to a row object, returning a new object with the results.
 * This is called after the user has finished editing a row in the bulk catalog modal.
 * @param {Object} row - The row object, which may have been edited by the user.
 * @param {Object} raw - The original import row object.
 * @param {Object} version - The original version object (if any), which may have been edited by the user.
 * @returns {Object} - The resulting row object with the overlay rules applied.
 */
    const applyCatalogueFallbackOnRow = (row) => {
        const cat = row?._linkItem || (row?.id ? idToItem.get(String(row.id)) : null);
        const raw = row?._importRaw ?? {};
        const version = getCurrentVersion(cat?.itemLookupVersions || []);

        const base = blankCatalogueForType(raw);

        if (cat) {
            const typeNameFromCat = typeIdToName.get(String(cat.itemLookupTypeId)) ?? null;
             if (typeNameFromCat) {
                base.itemLookupTypeId = cat.itemLookupTypeId;
                base.itemType = typeNameFromCat;
             }

            const supplierNameFromCat =
                cat.supplierName ??
                (cat.supplier && (cat.supplier.supplierName || cat.supplier.name)) ??
                supplierIdToName.get(String(cat.supplierId ?? cat.vendorId)) ??
                cat.vendor ??
                cat.supplier ??
                null;
            if (supplierNameFromCat) base.supplier = supplierNameFromCat;

            if (typeof cat.isInternal === "boolean") {
                base.isInternal = cat.isInternal;
            }

            const catTypeAttrs = normalizeAttrCollection(
                (version && version.typeCustomAttributes) ?? cat.typeCustomAttributes
            );
            const catCustAttrs = normalizeAttrCollection(
                (version && version.customAttributes) ?? cat.customAttributes
            );
            if (catTypeAttrs.length) base.typeCustomAttributes = catTypeAttrs;
            if (catCustAttrs.length) base.customAttributes = catCustAttrs;

            // Use catalogue values for descriptive/type metadata only.
            // Do not overwrite billing side from the catalogue version.
            if (version) {
                const shouldUseCatalogueSalesforceCodes = billingTypesMatchForSalesforce(row, version);

                base.itemLookupVersionId = version.id;
                if (version.isRecurring && !version.isUpfront) {
                    // base.recurringCost = toNumOrNull(version.recurringCost);
                    // base.recurringPrice = toNumOrNull(version.recurringPrice);
                    base.recurringStartMonth = toNumOrNull(version.recurringStartMonth) ?? toNumOrNull(raw.recurringStartMonth);
                    base.recurringIntervalMonths = toNumOrNull(version.recurringIntervalMonths) ?? toNumOrNull(raw.recurringIntervalMonths);
                    base.recurringNumPayments = toNumOrNull(version.recurringNumPayments) ?? toNumOrNull(raw.recurringNumPayments);
                }

                if (shouldUseCatalogueSalesforceCodes && version.isUpfront) {
                    base.upfrontSalesforceCode = version.upfrontSalesforceCode ?? raw.salesforceCode;
                }

                if (shouldUseCatalogueSalesforceCodes && version.isRecurring) {
                    base.recurringSalesforceCode = version.recurringSalesforceCode ?? raw.salesforceCode;
                }

                // If both upfront and recurring codes exist and are different, combine them with a pipe separator.
                if (shouldUseCatalogueSalesforceCodes) {
                    const versionUpCode = version.isUpfront ? (base.upfrontSalesforceCode ?? null) : null;
                    const versionRecCode = version.isRecurring ? (base.recurringSalesforceCode ?? null) : null;
                    if (versionUpCode && versionRecCode && versionUpCode !== versionRecCode) {
                        base.salesforceCode = `${versionUpCode} | ${versionRecCode}`;
                    } else {
                        base.salesforceCode = versionUpCode || versionRecCode || base.salesforceCode;
                    }
                }
            }
        }
        else {
            setItemLookupTypeByMappedNames(base, row)

            if ((raw.groupName.startsWith("Managed Service") ||
                 raw.groupName.startsWith("New Managed Service")) ) {

                if (['Assist', 'Maintain', 'Assure', 'Augment'].includes(raw.internalName)) {
                    if (!base.salesforceCode){
                        base.salesforceCode = "New Managed Services"
                        base.recurringSalesforceCode = base.salesforceCode
                    }
                }

                if (!raw.supplier) {
                    base.supplier = "Maintel"
                }
            }
        }

        base.description = "" // prevents showing the description column on quoteItemBulkImportModal        
        base.internalName = cat?.name ?? raw.internalName
        base.upfrontNameOnQuote = raw.upfrontNameOnQuote ?? null;
        base.recurringNameOnQuote = raw.recurringNameOnQuote ?? null;
        base.upfrontSalesforceCode = base.upfrontSalesforceCode ?? raw.upfrontSalesforceCode ?? null;
        base.recurringSalesforceCode = base.recurringSalesforceCode ?? raw.recurringSalesforceCode ?? null;
        base.autotaskCode = cat?.autotaskCode ?? raw.autotaskCode;
        base.salesforceCode = base.salesforceCode ?? raw.salesforceCode;
        if (isEmpty(base.supplier)) base.supplier = raw.supplier;        
        if (!isEmpty(raw.quantity)) base.quantity = toNumOrNull(raw.quantity);
        base.groupName = raw.groupName ?? null;

        if (raw && Object.prototype.hasOwnProperty.call(raw, "isInternal") && raw.isInternal != null) {
            base.isInternal = coerceBooleans(raw.isInternal);
        }
        else if (row?._userEdited?.isInternal === true && row.hasOwnProperty("isInternal")) {
            base.isInternal = coerceBooleans(row.isInternal);
        }

        const importTypeAttrs = normalizeAttrCollection(raw.typeCustomAttributes);
        if (importTypeAttrs.length) base.typeCustomAttributes = mergeAttrsByLabel(base.typeCustomAttributes, importTypeAttrs);

        const importCustAttrs = normalizeAttrCollection(raw.customAttributes);
        if (importCustAttrs.length) base.customAttributes = mergeAttrsByLabel(base.customAttributes, importCustAttrs);

        // Prefer explicit flags from mapped data; fall back to value/version inference.
        let up = false;
        let rec = false;

        if (row?.isUpfront === true || coerceBooleans(raw?.isUpfront)) up = true;
        if (row?.isRecurring === true || coerceBooleans(raw?.isRecurring)) rec = true;

        if (!up && !rec) {
            const fromValues = inferBillingFromValues(raw);
            rec = fromValues === "Recurring";
            up = !rec;
        }

        // If both flags are set, keep as Mixed (both sides) so both upfront/recurring values are preserved.

        base.isUpfront = up;
        base.isRecurring = rec;
        base.billingType = billingTypeFromFlags(up, rec);

        // Use raw v1 import values:
        if (!isEmpty(raw.upfrontCost)) base.upfrontCost = toNumOrNull(raw.upfrontCost);
        if (!isEmpty(raw.upfrontPrice)) base.upfrontPrice = toNumOrNull(raw.upfrontPrice);
        if (!isEmpty(raw.recurringCost)) base.recurringCost = toNumOrNull(raw.recurringCost);
        if (!isEmpty(raw.recurringPrice)) base.recurringPrice = toNumOrNull(raw.recurringPrice);
        if (!isEmpty(raw.recurringStartMonth)) base.recurringStartMonth = toNumOrNull(raw.recurringStartMonth);
        if (!isEmpty(raw.recurringIntervalMonths)) base.recurringIntervalMonths = toNumOrNull(raw.recurringIntervalMonths);
        if (!isEmpty(raw.recurringNumPayments)) base.recurringNumPayments = toNumOrNull(raw.recurringNumPayments);

        // --- USER OVERRIDES (from bulk modal) ---
        const preferUser = (key) => {
            return (row?._userEdited?.[key] === true)
        };

        // Simple scalar fields
        ["internalName", "upfrontNameOnQuote", "recurringNameOnQuote", "itemType", "supplier", "autotaskCode", "quantity", "isInternal",
            "recurringStartMonth", "recurringIntervalMonths", "recurringNumPayments",
            "upfrontCost", "upfrontPrice", "recurringCost", "recurringPrice" ,"groupName"].forEach(k => {
                if (preferUser(k)) base[k] = row[k];
            });

        if (preferUser("upfrontSalesforceCode")) {
            base.upfrontSalesforceCode = !isEmpty(row.upfrontSalesforceCode) ? row.upfrontSalesforceCode : null;
        }
        if (preferUser("recurringSalesforceCode")) {
            base.recurringSalesforceCode = !isEmpty(row.recurringSalesforceCode) ? row.recurringSalesforceCode : null;
        }

        // salesforceCode: only apply user override if it's actually non-empty, otherwise
        // fall back to the raw import value (guards against bug-artifact null user edits)
        if (preferUser("salesforceCode")) {
            base.salesforceCode = !isEmpty(row.salesforceCode) ? row.salesforceCode : raw.salesforceCode;
        }

        // If user forced billing flags/types, keep them
        if (preferUser("billingType")) {
            base.billingType = row.billingType || base.billingType;
            base.isUpfront = base.billingType === "Upfront";
            base.isRecurring = base.billingType === "Recurring";
        }       

        // Pricing & descriptions: keep explicit side edits
        if (preferUser("upfrontCost")) base.upfrontCost = toNumOrNull(row.upfrontCost);
        if (preferUser("upfrontPrice")) base.upfrontPrice = toNumOrNull(row.upfrontPrice);
        if (preferUser("recurringCost")) base.recurringCost = toNumOrNull(row.recurringCost);
        if (preferUser("recurringPrice")) base.recurringPrice = toNumOrNull(row.recurringPrice);
        if (preferUser("recurringStartMonth")) base.recurringStartMonth = toNumOrNull(row.recurringStartMonth);
        if (preferUser("recurringIntervalMonths")) base.recurringIntervalMonths = toNumOrNull(row.recurringIntervalMonths);
        if (preferUser("recurringNumPayments")) base.recurringNumPayments = toNumOrNull(row.recurringNumPayments);
        if (preferUser("upfrontNameOnQuote")) base.upfrontNameOnQuote = row.upfrontNameOnQuote ?? "";
        if (preferUser("recurringNameOnQuote")) base.recurringNameOnQuote = row.recurringNameOnQuote ?? "";
        if (preferUser("groupName")) base.groupName = row.groupName ?? "";
        // Type custom attributes: if user edited in bulk modal, keep them
        if (preferUser("typeCustomAttributes")) {
            base.typeCustomAttributes = normalizeAttrCollection(row.typeCustomAttributes);
        }

        if (preferUser("upfrontSalesforceCode") || preferUser("recurringSalesforceCode")) {
            const upCode = base.isUpfront ? (base.upfrontSalesforceCode ?? null) : null;
            const recCode = base.isRecurring ? (base.recurringSalesforceCode ?? null) : null;
            if (upCode && recCode && upCode !== recCode) {
                base.salesforceCode = `${upCode} | ${recCode}`;
            } else {
                base.salesforceCode = upCode || recCode || null;
            }
        }

        if (base.quantity < 0) {
            // if the quantity is less than zero, then we need to invert the costs and prices
            // (as they are negative in V1), and set as credit item with positive quantity
            // for NG
            if (base.upfrontCost != null) base.upfrontCost = Math.abs(base.upfrontCost);
            if (base.upfrontPrice != null) base.upfrontPrice = Math.abs(base.upfrontPrice);
            if (base.recurringCost != null) base.recurringCost = Math.abs(base.recurringCost);
            if (base.recurringPrice != null) base.recurringPrice = Math.abs(base.recurringPrice);

            base.quantity = -base.quantity; // make quantity positive
            base.isCreditItem = true;
        }

        // Non-integer quantity: scale costs/prices to a single-unit equivalent and
        // append the original quantity in brackets to descriptions so it stays visible.
        // e.g. quantity 0.5, cost 100 → quantity 1, cost 50, description "... (0.5)"
        const resolvedQty = base.quantity;
        if (resolvedQty != null && Number.isFinite(resolvedQty) && !Number.isInteger(resolvedQty) && resolvedQty > 0) {
            const quantityToTwoDP = resolvedQty.toFixed(2);
            const quantityClean = quantityToTwoDP.endsWith("0") ? quantityToTwoDP.slice(0, -1) : quantityToTwoDP; // If it ends with 0, remove the trailing zero for cleaner display
            const suffix = ` (${quantityClean})`;
            if (!isEmpty(base.upfrontNameOnQuote)) base.upfrontNameOnQuote = base.upfrontNameOnQuote + suffix;
            if (!isEmpty(base.recurringNameOnQuote)) base.recurringNameOnQuote = base.recurringNameOnQuote + suffix;
            if (base.upfrontCost != null) base.upfrontCost = base.upfrontCost * resolvedQty;
            if (base.upfrontPrice != null) base.upfrontPrice = base.upfrontPrice * resolvedQty;
            if (base.recurringCost != null) base.recurringCost = base.recurringCost * resolvedQty;
            if (base.recurringPrice != null) base.recurringPrice = base.recurringPrice * resolvedQty;
            base.quantity = 1;
        }

        // Keep quote-name fields aligned with billing side in the table model.
        // Prevent stale opposite-side names when an item is one-sided.
        if (base.isRecurring && !base.isUpfront) {
            base.upfrontNameOnQuote = "";
        } else if (base.isUpfront && !base.isRecurring) {
            base.recurringNameOnQuote = "";
        }

        return { ...row, ...base };
    };

    const setItemLookupId = (item) => {
           if (item._linkItem && item._linkItem.id) {
            return item._linkItem.id;
        }
    }

    const setItemLookupTypeByMappedNames = (base, row) => {
        if (itemTypes.length === 0) return;

        const rawItemId = String(row?.itemId ?? row?._importRaw?.itemId ?? "").trim();
        const isVirtualMachineItem = /^virtualmachine\./i.test(rawItemId);

        if (isVirtualMachineItem) {
            const virtualMachineType = itemTypes.find(t =>
                String(t.itemLookupTypeName || t.name || "").trim().toLowerCase() === "virtual machine"
            );

            if (virtualMachineType) {
                base.itemLookupTypeId = virtualMachineType.id;
                base.itemType = virtualMachineType.itemLookupTypeName || virtualMachineType.name;
                return;
            }
        }

        const mappedType = itemTypes.find(t =>
            t.mappedNames?.split(';').some(w => w.trim().toLowerCase() === String(base.itemType || '').trim().toLowerCase())
        );

        if (mappedType) {
            base.itemLookupTypeId = mappedType.id;
            base.itemType = mappedType.itemLookupTypeName;
        } else {
            const unitCostItemTypeId = 2;
            base.itemLookupTypeId = unitCostItemTypeId;
            base.itemType = typeIdToName.get(String(unitCostItemTypeId));
        }
    }

    // Helper to map a single row
    const mapSingleRow = (r, i) => {
        const src = normalizeRowKeys(r);
        let out = { ...src, __rowId: i, groupName: "" };
        out = dedupeCaseKeys(out);

        out._importRaw = {};
        for (const k of mergeableKeys) {
            out._importRaw[k] = src?.[k] ?? null;
        }

        const allLabels = itemTypes.flatMap(itemType => {
            try {
                const customAttributes = JSON.parse(itemType.customAttributes || '[]');
                return customAttributes.map(attr => attr.label);
            } catch (e) {
                console.warn('Invalid JSON in customAttributes:', itemType.customAttributes);
                return [];
            }
        });

        Object.keys(src).forEach((k) => {
            const canonKey = toCanonicalKey(k);
            registerLabel(canonKey, k);

            const normalizedKey = k.replace(/\s+/g, '').toLowerCase();
            const matchedItem = allLabels.find(x => x.replace(/\s+/g, '').trim().toLowerCase() === normalizedKey)
            const normalizedCanonKey = toCamelKey(canonKey);
            if (matchedItem && !String(canonKey).startsWith("_") && !CORE_FIELDS.has(normalizedCanonKey)) {
                src.typeCustomAttributes = src.typeCustomAttributes || [];
                src.typeCustomAttributes.push({ label: matchedItem, value: src[k] });
                delete out[k];
            }
        });

        const tacFromArray = normalizeAttrCollection(src.typeCustomAttributes);
        const tacFromCols = parseAttrColumnsFromRow(src, "typeCustomAttributes");
        const explicitTypeAttrs = mergeAttrsByLabel(tacFromArray, tacFromCols);
        const explicitLabelSet = new Set(explicitTypeAttrs.map(a => normAttrLabel(a.label).toLowerCase()));

        const tacFromScalar = Object.entries(src).flatMap(([k, v]) => {
            if (v == null || v === "") return [];
            const keyNorm = toCamelKey(k);
            if (!typeAttrLabelKeys.has(keyNorm)) return [];
            if (CORE_FIELDS.has(keyNorm)) return [];
            const label = typeAttrLabelMap.get(keyNorm) || normAttrLabel(k);
            if (explicitLabelSet.has(normAttrLabel(label).toLowerCase())) return [];
            return [{ label, value: v }];
        });

        out._importRaw.typeCustomAttributes = mergeAttrsByLabel(tacFromScalar, explicitTypeAttrs);

        if (typeof out.tags === "string") {
            out.tags = out.tags.split(",").map((s) => s.trim()).filter(Boolean);
        }
        ["quantity", "upfrontCost", "upfrontPrice", "recurringCost", "recurringPrice", "recurringStartMonth", "recurringIntervalMonths", "recurringNumPayments"].forEach((k) => {
            const n = Number(out[k]);
            out[k] = Number.isFinite(n) ? n : (out[k] ?? null);
        });
        ["isInternal", "isUpfront", "isRecurring", "disabled"].forEach((k) => {
            if (k in out) out[k] = coerceBooleans(out[k]);
        });

        out.billingType = deriveBillingType(out);
        out.id = null;
        out._linkStatus = "NEW";
        out._linkSelectedId = NEW_ITEM_SENTINEL;
        out._linkCandidateIds = [];
        out._linkResolved = false;

        return applyCatalogueFallbackOnRow(out);
    };

    // --- Calculate total pages ---
    const totalRecords = Array.isArray(rows) ? rows.length : 0;
    const totalPages = Math.max(1, Math.ceil(totalRecords / Math.max(1, tablePageSize)));

    // --- Map only current page rows ---
    useEffect(() => {
        let cancelled = false;

        if (!Array.isArray(rows) || rows.length === 0) {
            setMappedData([]);
            return;
        }

        // Calculate which rows belong to current page
        const start = (tablePageNumber - 1) * tablePageSize;
        const end = start + tablePageSize;
        const pageRows = rows.slice(start, end);

        // Map only the rows for this page, but keep their original rowIds
        const mappedPageRows = pageRows.map((r, pageIndex) => {
            const originalRowId = start + pageIndex; // Keep track of original index
            const mapped = mapSingleRow(r, originalRowId);
            // Store in the ref for later use (e.g., on import)
            allMappedRowsRef.current.set(originalRowId, mapped);
            return mapped;
        });

        if (!cancelled) {
            setMappedData(mappedPageRows);
        }

        return () => {
            cancelled = true;
        };
    }, [
        rows, tablePageNumber, tablePageSize,
        mergeableKeys, typeNameToDef, typeIdToName, supplierIdToName,
        typeAttrLabelKeys, typeAttrLabelMap, itemTypes
    ]);

    const resolveLinkForRow = useCallback(
        (rowId, selectedIdOrNull) => {
            setMappedData((prev) =>
                prev.map((r) => {
                    if (r.__rowId !== rowId) return r;

                    const selectedId = selectedIdOrNull ?? "";

                    if (selectedId === NEW_ITEM_SENTINEL) {
                        const importSalesforceCode = r?._importRaw?.salesforceCode ?? "";
                        const nextUserEdited = { ...(r._userEdited || {}) };
                        delete nextUserEdited.salesforceCode;

                        const next = { ...r };
                        next.id = null;
                        next._linkItem = null;
                        next._linkLabel = null;
                        next._linkStatus = "NEW";
                        next._linkSelectedId = NEW_ITEM_SENTINEL;
                        next._linkResolved = true;
                        next.salesforceCode = importSalesforceCode;
                        next._userEdited = nextUserEdited;
                        const result = applyCatalogueFallbackOnRow(next);
                        allMappedRowsRef.current.set(rowId, result);
                        return result;
                    }

                    const candidateItem = selectedId
                        ? (Array.isArray(r?._linkCandidateItems)
                            ? r._linkCandidateItems.find((item) => String(item?.id) === String(selectedId))
                            : null)
                        : null;
                    const sel = selectedId ? idToItem.get(String(selectedId)) || candidateItem : null;
                    const next = { ...r };

                    if (sel) {
                        if (!billingTypesMatchForLink(next, sel)) {
                            next.id = null;
                            next._linkItem = null;
                            next._linkLabel = null;
                            next._linkSelectedId = "";
                            next._linkStatus = "NOT_FOUND";
                            next._linkResolved = true;
                            const result = applyCatalogueFallbackOnRow(next);
                            allMappedRowsRef.current.set(rowId, result);
                            return result;
                        }

                        next.id = sel.id;
                        next._linkItem = sel;
                        next._linkLabel = labelForItem(sel);
                        next._linkKey = r._linkKey ?? null;
                        next._linkStatus = "OK";
                        next._linkMatchesCount = 1;
                        next._linkSelectedId = String(sel.id);
                        next._linkResolved = true;

                        // keep import/user-entered values (do NOT null itemType/supplier)

                        const set = new Set([...(next._linkCandidateIds || []), String(sel.id)]);
                        (next._linkMatchIds || []).forEach((id) => set.add(String(id)));
                        next._linkCandidateIds = Array.from(set);

                        void fetchActiveItemLookupVersionByItemLookupId(sel.id)
                            .then((activeVersion) => {

                                setMappedData((latest) =>
                                    latest.map((row) => {
                                        if (row.__rowId !== rowId) return row;
                                        if (String(row?._linkSelectedId || row?.id || "") !== String(sel.id)) return row;

                                        const updated = {
                                            ...row,
                                            upfrontCost: toNumOrNull(activeVersion?.upfrontCost),
                                            upfrontPrice: toNumOrNull(activeVersion?.upfrontPrice),
                                            recurringCost: toNumOrNull(activeVersion?.recurringCost),
                                            recurringPrice: toNumOrNull(activeVersion?.recurringPrice)
                                        };

                                        const result = applyCatalogueFallbackOnRow(updated);
                                        allMappedRowsRef.current.set(rowId, result);
                                        return result;
                                    })
                                );
                            })
                            .catch((error) => {
                                console.error("Error fetching active item lookup version:", error);
                            });

                        const result = applyCatalogueFallbackOnRow(next);
                        allMappedRowsRef.current.set(rowId, result);
                        return result;
                    } else {
                        if (selectedId && selectedId !== NEW_ITEM_SENTINEL) {
                            next.id = selectedId;
                            next._linkItem = null;
                            next._linkLabel = r?._linkLabel || `#${selectedId}`;
                            next._linkKey = r._linkKey ?? null;
                            next._linkSelectedId = String(selectedId);
                            next._linkStatus = "OK";
                            next._linkMatchesCount = 1;
                            next._linkResolved = true;
                            const set = new Set([...(next._linkCandidateIds || []), String(selectedId)]);
                            (next._linkMatchIds || []).forEach((id) => set.add(String(id)));
                            next._linkCandidateIds = Array.from(set);
                            const result = applyCatalogueFallbackOnRow(next);
                            allMappedRowsRef.current.set(rowId, result);
                            return result;
                        }

                        next.id = null;
                        next._linkItem = null;
                        next._linkLabel = null;
                        next._linkKey = null;
                        next._linkSelectedId = "";
                        next._linkStatus = (next._linkMatchIds?.length ?? 0) > 0 ? "CONFLICT" : "NOT_FOUND";
                        next._linkResolved = true;
                        const result = applyCatalogueFallbackOnRow(next);
                        allMappedRowsRef.current.set(rowId, result);
                        return result;
                    }
                })
            );
        },
        [idToItem, billingTypesMatchForLink]
    );

    useEffect(() => {
        if (!mappedData?.length) return;
        setMappedData((prev) =>
            prev.map((r) => {
                const processed = applyCatalogueFallbackOnRow(r);
                allMappedRowsRef.current.set(processed.__rowId, processed);
                return processed;
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idToItem, itemTypes, suppliersData]);
    
    // Memoize just the set of custom attribute labels (not the full column objects)
    const customAttrLabels = useMemo(() => {
        const labels = new Set();
        for (const r of mappedData || []) {
            (r.customAttributes || []).forEach((a) => {
                const lbl = normAttrLabel(a.label);
                if (lbl) labels.add(lbl);
            });
        }
        return Array.from(labels);
    }, [mappedData]);

    // Build custom attribute columns from the labels
    const customAttrColumns = useMemo(() => {
        return customAttrLabels.map((label) => {
            const key = `custAttr__${slugKey(label)}`;
            return {
                key,
                label: `Attr • ${label}`,
                render: (_, row) => {
                    const v = (row.customAttributes || [])
                        .find((a) => normAttrLabel(a.label).toLowerCase() === label.toLowerCase());
                    return v?.value ?? "";
                },
            };
        });
    }, [customAttrLabels]);

    useEffect(() => {
        if (!mappedData?.length) return;

        const categoryNames = [...new Set([
                "Professional Services",
                "Infrastructure",
                ...mappedData.map(item => item.categoryName)
            ])
        ];
        setGroupNames(prev => {
            return [...new Set([...prev, ...categoryNames])];
        });

    }, [mappedData]);

    const mappedColumns = useMemo(() => {
        // Start with dynamic keys discovered in the data
        // Use only first row for structure (lighter than scanning all rows on every change)
        const base = mappedData?.length ? buildDynamicColumns(mappedData) : [];

        const orderCol = {
            key: "order", 
            label: "Order",
            isFixed: true,
            style: { width: "30px" },
            group: "Selection",
            className: "text-center text-muted",
            // Display 1-based index, but keep the underlying key numeric for sorting
            render: (_, row) => row.__rowId + 1
        };

        const groupNameCol = {
            key: "groupName",
            label: "Group Name",
            isSystem: true,
            className: "p-1",
            style: { width: "275px" },
            group: "Selection",
            render: (_, row) => (
                <div style={{ width: "100%" }}>
                    <CustomCreatableSelect
                        options={groupNames.map((gn) => ({ value: gn, label: gn }))}
                        value={row.groupName || ""}
                        placeholder="Please Select"
                        onChange={(opt) => {
                            const val = String(opt?.value ?? "").trim();
                            if (val) {
                                setGroupNames((prev) => {
                                    const exists = prev.some((g) => String(g).trim().toLowerCase() === val.toLowerCase());
                                    return exists ? prev : [...prev, val];
                                });
                            }
                            setMappedData(prev =>
                                prev.map(r => {
                                    if (r.__rowId !== row.__rowId) return r;
                                    const processed = applyCatalogueFallbackOnRow({
                                        ...r,
                                        groupName: val,
                                        _userEdited: { ...(r._userEdited || {}), groupName: true }
                                    });
                                    allMappedRowsRef.current.set(processed.__rowId, processed);
                                    return processed;
                                })
                            );
                        }}
                    />
                </div>
            ),
        };

        // Enhance special fields with custom renders (currency, catalogue link, etc.)
        const byKey = Object.fromEntries(base.map(c => [c.key, { ...c }]));

        // Ensure catalogueLink is consistently configured even if it already exists from dynamic columns.
        if (!byKey.catalogueLink) {
            byKey.catalogueLink = { key: "catalogueLink" };
        }
        byKey.catalogueLink = {
            ...byKey.catalogueLink,
            key: "catalogueLink",
            label: "Catalogue",
            isFixed: true,
            style: { width: "370px", minWidth: "370px", maxWidth: "370px" },
            group: "Selection"
        };
        byKey.catalogueLink.render = (_, row) => {            
            const status = row?._linkStatus;
            const idAttr = `catlink-${row.__rowId}`;
            const label = row?._linkLabel || "New Item";

            const rowCandidateOptions = Array.isArray(row?._linkCandidateOptions)
                ? row._linkCandidateOptions
                : [];

            const rowCandidateOptionMap = new Map(
                rowCandidateOptions
                    .filter((opt) => opt?.value != null)
                    .map((opt) => [String(opt.value), opt])
            );

            const candidateIds = Array.from(
                new Set(
                    [
                        ...rowCandidateOptions.map((opt) => String(opt.value)),
                        ...(row._linkCandidateIds || []),
                        ...((row._linkMatchIds || []).map(String)),
                        row._linkedItemId != null ? String(row._linkedItemId) : null,
                    ].filter(Boolean)
                )
            );

            const optionFromId = (cid) => {
                const cidStr = String(cid);
                const fromRow = rowCandidateOptionMap.get(cidStr);
                if (fromRow?.label) return { value: cidStr, label: fromRow.label };

                // If this is the current matched/selected ID, use the link label if available
                // but strip options (keep only nameOnQuote/name part)
                const currentId = String(row._linkSelectedId || row._linkedItemId || "");
                if (cidStr === currentId && row._linkLabel) {
                    const nameOnQuoteOnly = row._linkLabel.split(", ")[0]; // Strip options
                    return { value: cidStr, label: nameOnQuoteOnly };
                }

                const it = idToItem.get(cidStr);
                const lbl = it
                    ? (it.upfrontNameOnQuote || it.recurringNameOnQuote || it.name || `#${it.id}`)
                    : `#${cid}`;
                return { value: cidStr, label: lbl };
            };

            const options = [
                ...candidateIds.map(optionFromId),
                { value: NEW_ITEM_SENTINEL, label: "New Item" },
            ];

            const selectValue = row._linkSelectedId
                ? String(row._linkSelectedId)
                : row._linkedItemId != null
                    ? String(row._linkedItemId)
                    : (status === "NEW" || status === "NOT_FOUND" || status === "NO_ROW_VALUES")
                        ? NEW_ITEM_SENTINEL
                        : "";

            if (["OK", "NEW", "NOT_FOUND", "NO_ROW_VALUES"].includes(status)) {
                const tooltipId = `${idAttr}-tip`;
                return (
                    <div className="d-flex align-items-center gap-2">
                        <span id={tooltipId} className="d-inline-flex align-items-center gap-1">
                            <i
                                className={`bi ${status === "NEW"
                                    ? "bi-plus-circle text-primary"
                                    : status === "OK"
                                        ? "bi-link-45deg text-success"
                                        : "bi-search text-muted"
                                    }`}
                            />
                        </span>
                        <div style={{ width: 280, minWidth: 280, maxWidth: 280 }}>
                            <CustomSelect
                                isMulti={false}
                                options={options}
                                value={selectValue}
                                placeholder={
                                    status === "NEW"
                                        ? "New Item"
                                        : status === "OK"
                                            ? "Linked item"
                                            : "Choose item…"
                                }
                                onChange={(opt) => {
                                    resolveLinkForRow(row.__rowId, opt ? opt.value : null);
                                }}
                            />
                        </div>
                        <Button
                            id={`${idAttr}-searchbtn`}
                            color="link"
                            size="sm"
                            className="p-0"
                            title="Search catalogue"
                            onClick={() => openSearch(row)}
                        >
                            <i className="bi bi-search" />
                            <span className="visually-hidden">Search catalogue</span>
                        </Button>
                        <UncontrolledTooltip placement="top" target={tooltipId}>
                            {status === "NEW" ? (
                                <div className="text-start">
                                    <div><strong>New item</strong></div>
                                    <div>This row will be created as a new item.</div>
                                </div>
                            ) : status === "OK" && row?._linkItem ? (
                                <div className="text-start">
                                    {(row._linkItem.name) && (
                                        <div><strong>{row._linkItem.name}</strong></div>
                                    )}
                                    <div>ID: {row._linkItem.id}</div>
                                    {row._linkItem.options && <div>Options: {row._linkItem.options}</div>}
                                    {row._linkItem.autotaskCode && <div>Autotask: {row._linkItem.autotaskCode}</div>}
                                    {row._linkItem.salesforceCode && <div>Salesforce: {row._linkItem.salesforceCode}</div>}
                                    {typeof row._linkItem.isInternal === "boolean" && (
                                        <div>Internal: {row._linkItem.isInternal ? "Yes" : "No"}</div>
                                    )}
                                    {typeof row._linkItem.isCreditItem === "boolean" && (
                                        <div>Credit Item: {row._linkItem.isCreditItem ? "Yes" : "No"}</div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-start">
                                    <div><strong>No exact match</strong></div>
                                    <div>Select “New Item” to create during import.</div>
                                </div>
                            )}
                        </UncontrolledTooltip>
                    </div>
                );
            }

            if ((row?._linkMatchIds?.length ?? 0) > 0) {
                const tooltipId = `${idAttr}-tip`;
                const optionsWithNew = [
                    ...candidateIds.map((cid) => optionFromId(cid)),
                    { value: NEW_ITEM_SENTINEL, label: "New Item" },
                ];
                return (
                    <div className="d-flex align-items-center gap-2">
                        <span id={tooltipId} className="d-inline-flex align-items-center gap-1">
                            <i className="bi bi-exclamation-triangle-fill text-danger" />
                        </span>
                        <div style={{ width: 280, minWidth: 280, maxWidth: 280 }}>
                            <CustomSelect
                                isMulti={false}
                                isClearable
                                options={optionsWithNew}
                                value={row._linkSelectedId || row._linkedItemId ? String(row._linkSelectedId || row._linkedItemId) : ""}
                                placeholder={row?._linkMatchesCount ? `Conflict (${row._linkMatchesCount})` : "Conflict"}
                                onChange={(opt) => {
                                    resolveLinkForRow(row.__rowId, opt ? opt.value : null);
                                }}
                            />
                        </div>
                        <Button
                            id={`${idAttr}-searchbtn`}
                            color="link"
                            size="sm"
                            className="p-0"
                            title="Search catalogue"
                            onClick={() => openSearch(row)}
                        >
                            <i className="bi bi-search" />
                            <span className="visually-hidden">Search catalogue</span>
                        </Button>
                        <UncontrolledTooltip placement="top" target={tooltipId}>
                            <div className="text-start">
                                <div><strong>Multiple matches</strong></div>
                                <div>Matching key: <span style={{color: "green", fontWeight: "bold"}}>{row?._linkKey}</span></div>
                                <div>Pick one, or choose “New Item”.</div>
                            </div>
                        </UncontrolledTooltip>
                    </div>
                );
            }

            const iconClass =
                status === "OK"
                    ? "bi-link-45deg text-success"
                    : status === "CONFLICT"
                        ? "bi-exclamation-triangle-fill text-danger"
                        : status === "NEW"
                            ? "bi-plus-circle text-primary"
                            : "bi-search text-muted";

            const cellText =
                status === "OK"
                    ? label
                    : status === "CONFLICT"
                        ? `Conflict (${row?._linkMatchesCount})`
                        : "New Item";

            return (
                <span id={idAttr} className="d-inline-flex align-items-center gap-1">
                    <i className={`bi ${iconClass} me-2`} />
                    <span>{cellText}</span>
                </span>
            );
        };

        // Currency fields 
        for (const k of ["upfrontCost", "upfrontPrice", "recurringCost", "recurringPrice"]) {
            if (byKey[k]) {
                byKey[k] = {
                    ...byKey[k],
                    className: "p-1",
                    style: { width: "180px", minWidth: "180px" },
                    render: (_, row) => (
                        <InputGroup>
                            <InputGroupText>£</InputGroupText>
                            <Input disabled className="px-3 text-end" type="text" value={formatCurrency(row[k])} />
                        </InputGroup>
                    ),
                };
            }
        }

        // Narrow numeric fields
        for (const k of ["quantity", "recurringStartMonth", "recurringIntervalMonths", "recurringNumPayments"]) {
            if (byKey[k]) {
                byKey[k] = {
                    ...byKey[k],
                    className: "p-1 text-end",
                    style: { width: "100px", minWidth: "100px", whiteSpace: "normal" },
                };
            }
        }

        // isInternal checkbox
        if (byKey.isInternal) {
            byKey.isInternal = {
                ...byKey.isInternal,
                className: "text-center",
                style: { width: "80px", minWidth: "80px" },
                render: (value, row) => (
                    <Input
                        type="checkbox"
                        checked={!!row.isInternal}
                        onChange={(e) => {
                            const nextVal = !!e.target.checked;
                            setMappedData(prev =>
                                prev.map(r =>
                                    r.__rowId === row.__rowId
                                        ? applyCatalogueFallbackOnRow({
                                            ...r,
                                            isInternal: nextVal,
                                            _userEdited: { ...(r._userEdited || {}), isInternal: true }
                                        })
                                        : r
                                )
                            );
                        }}
                        onClick={(e) => e.stopPropagation()}
                        aria-label="Mark as internal"
                    />
                ),
            };
        }

        if (byKey.isCreditItem) {
            byKey.isCreditItem = {
                ...byKey.isCreditItem,
                className: "text-center",
                style: { width: "80px", minWidth: "80px" },
                render: (value, row) => (
                    <Input
                        type="checkbox"
                        checked={!!row.isCreditItem}
                        onChange={(e) => {
                            const nextVal = !!e.target.checked;
                            setMappedData(prev =>
                                prev.map(r =>
                                    r.__rowId === row.__rowId
                                        ? applyCatalogueFallbackOnRow({
                                            ...r,
                                            isCreditItem: nextVal,
                                            _userEdited: { ...(r._userEdited || {}), isCreditItem: true }
                                        })
                                        : r
                                )
                            );
                        }}
                        onClick={(e) => e.stopPropagation()}
                        aria-label="Mark as credit"
                    />
                ),
            };
        }

        // Actions column at the end
        const actionCols = [
            {
                key: "disabled",
                label: "Included?",
                group: "",
                isSystem: true,
                isDefault: true,
                isFixed: true,
                pinLocked: true,
                style: { width: "75px" },
                fixDirection: "right",
                className: "p-1 text-end",
                render: (_, row) => (
                    <FormGroup switch className="ms-3">
                        <Input
                            type="switch"
                            checked={!row.disabled}
                            onChange={(e) => {
                                const nextVal = !e.target.checked;
                                setMappedData(prev =>
                                    prev.map(r =>
                                        r.__rowId === row.__rowId
                                            ? applyCatalogueFallbackOnRow({
                                                ...r,
                                                disabled: nextVal,
                                                _userEdited: { ...(r._userEdited || {}), disabled: true }
                                            })
                                            : r
                                    )
                                );
                            }}
                            onClick={(e) => e.stopPropagation()}
                            aria-label="Mark as excluded"
                        />
                    </FormGroup>
                )
            },
            {
                key: "actions",
                label: "Edit",
                group: "",
                isSystem: true,
                isDefault: true,
                isFixed: true,
                pinLocked: true,
                style: { width: "30px" },
                fixDirection: "right",
                className: "p-1",
                render: (_, row) => (
                    <>
                        <Button outline onClick={() => openBulkWithItems([allMappedRowsRef.current.get(row.__rowId) || row])}>
                            <i className="bi bi-pencil" />
                        </Button>
                    </>
                )
            }
        ];

        // Merge final list: select → catalogueLink → dynamic columns → type/custom attrs → actions
        const orderedKeys = [
            "catalogueLink",
            ...Object.keys(byKey).filter(k => k !== "catalogueLink" && k !== "order"),
        ];
        const dynamicCols = orderedKeys.map(k => byKey[k]).filter(Boolean);

        const allColumns = [
            ...([orderCol, groupNameCol, ...dynamicCols].map(c => ({ ...c, group: c.group !== undefined ? c.group : "Main" }))),
            ...([...customAttrColumns].map(c => ({ ...c, group: "Attributes" }))),
            ...actionCols
        ].map(c => ({ ...c, isDefault: true }));

        return allColumns;
    }, [
        customAttrLabels,             // recompute when custom attrs change, not on every row resolution
        openBulkWithItems, applyCatalogueFallbackOnRow,        
        formatCurrency, customAttrColumns,
        idToItem, resolveLinkForRow, typeNameToDef, openSearch,
        groupNames
    ]);

    const searchableRowKeys = useMemo(
        () => (mappedColumns || [])
            .map((c) => c?.key)
            .filter((k) => !!k && !["select", "catalogueLink", "actions"].includes(k) && !String(k).startsWith("_")),
        [mappedColumns]
    );

    const filteredMappedData = useMemo(() => {
        const term = String(tableSearchTerm || "").trim().toLowerCase();
        if (!term) return mappedData || [];

        return (mappedData || []).filter((row) =>
            searchableRowKeys.some((key) => {
                const val = row?.[key];
                if (val == null) return false;
                if (Array.isArray(val)) return val.join(" ").toLowerCase().includes(term);
                if (typeof val === "object") return false;
                return String(val).toLowerCase().includes(term);
            })
        );
    }, [mappedData, searchableRowKeys, tableSearchTerm]);

    const sortedMappedData = useMemo(() => {
        const key = tableSortOptions?.key;
        const direction = tableSortOptions?.direction === "descending" ? -1 : 1;
        if (!key) return filteredMappedData;

        const copy = [...filteredMappedData];
        copy.sort((a, b) => {
            const av = a?.[key];
            const bv = b?.[key];

            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;

            if (typeof av === "number" && typeof bv === "number") {
                return (av - bv) * direction;
            }

            return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" }) * direction;
        });
        return copy;
    }, [filteredMappedData, tableSortOptions]);

    // Since mappedData is already paginated, sortedMappedData IS the current page
    // pagedMappedData should just be sortedMappedData (no additional pagination needed)
    const pagedMappedData = sortedMappedData;

    useEffect(() => {
        let cancelled = false;

        const resolveVisibleRows = async () => {
            if (!pagedMappedData?.length) return;

            const visibleUnresolved = pagedMappedData.filter((r) => !r?._linkResolved);
            if (visibleUnresolved.length === 0) return;

            for (const visibleRow of visibleUnresolved) {
                if (cancelled) return;

                const link = linkRowExact(visibleRow);
                if (cancelled) return;

                setMappedData((prev) =>
                    prev.map((r) => {
                        if (r.__rowId !== visibleRow.__rowId) return r;
                        if (r._linkResolved) return r;

                        const next = { ...r };
                        if (link.status === "OK") {
                            next.id = link.id;
                            next._linkStatus = "OK";
                            next._linkMatchesCount = 1;
                            next._linkLabel = link.label;
                            next._linkKey = link.key;
                            next._linkItem = link.item;
                            next._linkSelectedId = String(link.id);
                            next._linkCandidateIds = [String(link.id)];
                        } else if (link.status === "CONFLICT") {
                            next.id = null;
                            next._linkStatus = "CONFLICT";
                            next._linkMatchesCount = link.count;
                            next._linkKey = link.key;
                            next._linkMatchIds = link.ids?.map(String);
                            next._linkCandidateOptions = link.candidates || [];
                            next._linkCandidateItems = (link.candidates || []).map((candidate) => candidate.item).filter(Boolean);
                            next._linkSelectedId = "";
                            next._linkCandidateIds = [...(next._linkMatchIds || [])];
                        } else {
                            next.id = null;
                            next._linkStatus = "NEW";
                            next._linkSelectedId = NEW_ITEM_SENTINEL;
                            next._linkCandidateIds = [];
                        }

                        next._linkResolved = true;
                        const processed = applyCatalogueFallbackOnRow(next);
                        allMappedRowsRef.current.set(processed.__rowId, processed);
                        return processed;
                    })
                );
            }
        };

        resolveVisibleRows();
        return () => {
            cancelled = true;
        };
    }, [pagedMappedData, linkRowExact]);

    const tableDataWithStableId = useMemo(() => {
        return (pagedMappedData || []).map((row) => ({
            ...row,
            id: row.__rowId,
            _linkedItemId: row.id,
        }));
    }, [pagedMappedData]);

    // Decide billing for a mapped row in a single place
    const decideBilling = (row) => {
        // 1) Explicit label
        if (row.billingType === "Mixed") return { isUpfront: true, isRecurring: true };
        if (row.billingType === "Upfront") return { isUpfront: true, isRecurring: false };
        if (row.billingType === "Recurring") return { isUpfront: false, isRecurring: true };

        // 2) Booleans from row
        const upFlag = row.isUpfront === true;
        const recFlag = row.isRecurring === true;
        if (upFlag || recFlag) return { isUpfront: upFlag, isRecurring: recFlag };

        // 3) Infer from values (both if both present)
        const hasUp = Number(row.upfrontPrice ?? row.upfrontCost) > 0;
        const hasRec = Number(row.recurringPrice ?? row.recurringCost) > 0;
        if (hasUp || hasRec) return { isUpfront: !!hasUp, isRecurring: !!hasRec };

        // 4) Default
        return { isUpfront: true, isRecurring: false };
    };

    const handleAddItem = useCallback(
        (itemOrId) => {
            const id =
                typeof itemOrId === "object"
                    ? itemOrId?.id ?? itemOrId?.Id ?? itemOrId?.ID
                    : itemOrId;
            if (id == null || searchRow == null) {
                setIsSearchOpen(false);
                return;
            }

            const idStr = String(id);

            if (typeof itemOrId === "object" && itemOrId) {
                setCatalogueCache((prev) => {
                    const next = new Map(prev);
                    next.set(idStr, itemOrId);
                    return next;
                });
            }

            if (idToItem.has(idStr)) {
                resolveLinkForRow(searchRow.__rowId, idStr);
            } else {
                const fallbackLabel =
                    typeof itemOrId === "object" && (itemOrId.upfrontNameOnQuote || itemOrId.recurringNameOnQuote || itemOrId.name)
                        ? itemOrId.upfrontNameOnQuote || itemOrId.recurringNameOnQuote || itemOrId.name
                        : `#${idStr}`;

                setMappedData((prev) =>
                    prev.map((r) => {
                        if (r.__rowId !== searchRow.__rowId) return r;
                        const next = { ...r };
                        next.id = id;
                        next._linkStatus = "OK";
                        next._linkSelectedId = idStr;
                        next._linkLabel = fallbackLabel;
                        next._linkItem = null;
                        next._linkMatchesCount = 1;
                        next._linkResolved = true;
                        const set = new Set([...(next._linkCandidateIds || []), idStr]);
                        (next._linkMatchIds || []).forEach((cid) => set.add(String(cid)));
                        next._linkCandidateIds = Array.from(set);
                        const processed = applyCatalogueFallbackOnRow(next);
                        allMappedRowsRef.current.set(processed.__rowId, processed);
                        return processed;
                    })
                );
            }

            setIsSearchOpen(false);
        },
        [idToItem, searchRow, resolveLinkForRow]
    );

    useEffect(() => {
        console.log("mappedData:", mappedData);
        console.log("mappedColumns:", mappedColumns);
        console.log("items:", items);
    }, [mappedData, items, mappedColumns]);

    const handleImport = async () => {
        // Keep the backing ref synchronized with rows currently rendered in the table.
        (mappedData || []).forEach((row) => {
            if (row?.__rowId != null) {
                allMappedRowsRef.current.set(row.__rowId, row);
            }
        });

        // Make sure all rows have been mapped (process any unvisited pages)
        if (allMappedRowsRef.current.size < totalRecords) {
            // Process all remaining pages
            for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
                const start = (pageNum - 1) * tablePageSize;
                const end = start + tablePageSize;
                const pageRows = rows.slice(start, end);
                
                pageRows.forEach((r, pageIndex) => {
                    const originalRowId = start + pageIndex;
                    if (!allMappedRowsRef.current.has(originalRowId)) {
                        const mapped = mapSingleRow(r, originalRowId);
                        allMappedRowsRef.current.set(originalRowId, mapped);
                    }
                });
            }
        }

        // Ensure rows in the ref are in their latest derived state before validation/import.
        allMappedRowsRef.current.forEach((row, rowId) => {
            const processed = applyCatalogueFallbackOnRow(row);
            allMappedRowsRef.current.set(rowId, processed);
        });

        const hasUnresolvedConflicts = Array.from(allMappedRowsRef.current.values())
            .some((row) => row?._linkStatus === "CONFLICT");
        if (hasUnresolvedConflicts) {
            toast.error("Please resolve all conflicts before importing");
            return;
        }

        if (!validateItems()) return;

        toggle()

        //var groupName = "Import " + formatDateTime(new Date())

        // Get all mapped rows (across all pages) and sort by original row ID
        const allImportItems = Array.from(allMappedRowsRef.current.values())
            .sort((a, b) => a.__rowId - b.__rowId);

        const newQuoteItems = []

        allImportItems.forEach(importItem => {
            const { isUpfront, isRecurring } = decideBilling(importItem)
            var internalName = importItem.internalName || importItem.upfrontNameOnQuote || importItem.recurringNameOnQuote || "Unknown"

            var newItem = getBlankQuoteItem()

            newItem.source="v1 Import"

            newItem.quantity = importItem.quantity
            newItem.quoteGroupDescription = importItem.groupName || ""
            newItem.internalName = internalName
            newItem.recurringNameOnQuote = isRecurring ? (importItem.recurringNameOnQuote ?? internalName) : ""  
            newItem.upfrontNameOnQuote = isUpfront ? (importItem.upfrontNameOnQuote ?? internalName) : ""
            newItem.originalNameOnQuote = ""
            newItem.originalUpfrontNameOnQuote = newItem.upfrontNameOnQuote
            newItem.originalRecurringNameOnQuote = newItem.recurringNameOnQuote
            newItem.autotaskCode = importItem.autotaskCode || ""
            newItem.salesforceCode = importItem.salesforceCode || ""

            // Prefer explicit supplierId; otherwise derive from supplier name (case-insensitive)
            let derivedSupplierId = importItem?.supplierId ?? null;
            if (!derivedSupplierId) {
                const supplierNameCandidate =
                    (importItem?.supplier && String(importItem.supplier)) ||
                    (importItem?.supplierName && String(importItem.supplierName)) ||
                    (importItem?._linkItem?.supplierName && String(importItem._linkItem.supplierName)) ||
                    null;                    
                if (supplierNameCandidate) {
                    const key = supplierNameCandidate.trim().toLowerCase();
                    const lookedUp = supplierNameToId.get(key);
                    if (lookedUp != null) derivedSupplierId = lookedUp;
                }
            }
            newItem.supplierId = derivedSupplierId;

            newItem.itemLookupTypeId = importItem.itemLookupTypeId;
            newItem.itemLookupVersionId = importItem.itemLookupVersionId ?? null;
            newItem.itemLookupId = setItemLookupId(importItem);

            newItem.isInternal = !!importItem.isInternal
            newItem.disabled = coerceBooleans(importItem.disabled)

            newItem.isUpfront = isUpfront
            newItem.isRecurring = isRecurring
            newItem.billingType = billingTypeFromFlags(isUpfront, isRecurring)

            const num = (v) => (v == null || v === "" ? 0 : Number(v) || 0)

            const originalUpfrontPrice = num(importItem.originalUpfrontPrice)
            const upPrice = num(importItem.upfrontPrice)
            const upCost = num(importItem.upfrontCost)
            const originalRecurringPrice = num(importItem.originalRecurringPrice)
            const recPrice = num(importItem.recurringPrice)
            const recCost = num(importItem.recurringCost)

            newItem.originalRecurringPrice = isRecurring ? originalRecurringPrice : 0
            newItem.recurringPrice = isRecurring ? recPrice : 0
            newItem.originalUpfrontPrice = isUpfront ? originalUpfrontPrice : 0
            newItem.upfrontPrice = isUpfront ? upPrice : 0

            newItem.originalRecurringCost = isRecurring ? recCost : 0
            newItem.recurringCost = isRecurring ? recCost : 0
            newItem.originalUpfrontCost = isUpfront ? upCost : 0
            newItem.upfrontCost = isUpfront ? upCost : 0
            // Description → correct quote field (mirrors Cost/Price split)
            // Prefer nameOnQuote, then the specific side, then the generic description
            const baseDesc =
                importItem.upfrontNameOnQuote ||
                importItem.recurringNameOnQuote ||
                importItem.internalName ||
                "";

            if (isUpfront) {
                newItem.upfrontNameOnQuote = importItem.upfrontNameOnQuote || baseDesc;
                newItem.recurringNumPayments = null;
                newItem.recurringIntervalMonths = null;
                newItem.recurringStartMonth = null;
                newItem.upfrontSalesforceCode = importItem.upfrontSalesforceCode || importItem.salesforceCode || "";

                if (!isRecurring) newItem.recurringNameOnQuote = "";
            }

            if (isRecurring) {
                newItem.recurringNumPayments = importItem.recurringNumPayments ?? 1;
                newItem.recurringIntervalMonths =  1;
                newItem.recurringStartMonth = 1;

                newItem.recurringNameOnQuote = importItem.recurringNameOnQuote || baseDesc;
                newItem.recurringSalesforceCode = importItem.recurringSalesforceCode || importItem.salesforceCode || "";
                if (!isUpfront) newItem.upfrontNameOnQuote = "";
            }

            // If neither Upfront nor Recurring is set (unlikely), fall back to upfront
            if (!isUpfront && !isRecurring) {
                newItem.upfrontNameOnQuote =
                    importItem.upfrontNameOnQuote || baseDesc;
                newItem.recurringNameOnQuote = "";
            }

            newItem.isCreditItem = importItem.isCreditItem;
            newItem.customAttributes.push({ itemId: importItem.itemId });

            // ---- Type Custom Attributes ----
            // ---- Type Custom Attributes (coerce booleans based on the item type schema) ----
            const selectedTypeId = newItem.itemLookupTypeId;

            // find the item type definition that applies to this row
            const typeDef = (itemTypes || []).find(t => String(t.id) === String(selectedTypeId));

            // parse that type's attribute schema
            let typeAttrDefs = [];
            try {
                typeAttrDefs = typeDef?.customAttributes ? JSON.parse(typeDef.customAttributes) : [];
            } catch { /* ignore bad JSON */ }

            // collect labels that are boolean for quick lookup
            const booleanLabels = new Set(
                (typeAttrDefs || [])
                    .filter(def => String(def.fieldType).toLowerCase() === "boolean")
                    .map(def => String(def.label || def.name || def.key || "").trim().toLowerCase())
            );

            // Cope with incoming being either JSON string or an array
            const normalizedTypeAttrs = normalizeAttrCollection(importItem.typeCustomAttributes);

            // coerce values to real booleans for those labels
            const coercedTypeAttrs = normalizedTypeAttrs.map(a => {
                const label = String(a?.label ?? "").trim();
                let value = a?.value;

                if (booleanLabels.has(label.toLowerCase())) {
                    value = coerceBooleans(value); // uses your existing helper: true/"true"/"yes"/"1" → true
                }

                return { label, value };
            });

            newItem.typeCustomAttributes = coercedTypeAttrs;

            // Ensure service input is always object/array shapes, never JSON strings.
            newItem.customAttributes = normalizeAttrCollection(newItem.customAttributes);
            newItem.typeCustomAttributes = normalizeAttrCollection(newItem.typeCustomAttributes);
            newItem.tags = Array.isArray(newItem.tags) ? newItem.tags : [];

            newQuoteItems.push({...newItem, notes: title });
        });

        addQuoteItems(newQuoteItems);
    }
    
    const validateItems = () => {
        let returnOK = true;
        // Validate all rows across all pages (from the ref)
        const allItems = Array.from(allMappedRowsRef.current.values())
            .sort((a, b) => a.__rowId - b.__rowId);
        
        allItems.forEach(item => {
            let missingFields = []
            if (!item.autotaskCode)
                missingFields.push("Autotask Code")
            if (!item.groupName)
                missingFields.push("Group Name")
            if (missingFields.length > 0) {
                toast.error(`Missing fields for ${item.internalName || item.upfrontNameOnQuote || item.recurringNameOnQuote}: ${missingFields.join(", ")}`);
                returnOK = false
            }
        })
        return returnOK;
    }

    const handleBulkSave = useCallback((updatedRows) => {
        const byId = new Map((updatedRows || []).map(r => [r.__rowId, r]));

        // fields we want to respect as user overrides when changed
        const EDITABLE_FIELDS = new Set([
            "internalName", "quantity", "isInternal",
            "supplier", "supplierId", "itemType", "itemLookupTypeId",
            "autotaskCode", "salesforceCode", "upfrontSalesforceCode", "recurringSalesforceCode",
            "isUpfront", "isRecurring", "billingType",
            "upfrontCost", "upfrontPrice", "recurringCost", "recurringPrice",
            "upfrontNameOnQuote", "recurringNameOnQuote",
            "recurringStartMonth", "recurringIntervalMonths", "recurringNumPayments",
            "typeCustomAttributes", "groupName"
        ]);

        setMappedData(prev =>
            prev.map(orig => {
                const upd = byId.get(orig.__rowId);
                if (!upd) return orig;

                // compute per-field changes to set _userEdited flags
                const userEdited = { ...(orig._userEdited || {}) };
                for (const k of Object.keys(upd)) {
                    if (!EDITABLE_FIELDS.has(k)) continue;
                    const oldVal = orig[k];
                    const newVal = upd[k];
                    const changed = JSON.stringify(oldVal ?? null) !== JSON.stringify(newVal ?? null);
                    if (changed) userEdited[k] = true;
                }

                const merged = { ...orig, ...upd, _userEdited: userEdited };
                const processed = applyCatalogueFallbackOnRow(merged);
                
                // Also update in the ref so all pages have the latest changes
                allMappedRowsRef.current.set(orig.__rowId, processed);
                
                return processed;
            })
        );
    }, []);

    return (
        <div className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
            <ModalHeader
                toggle={toggle}
                close={(
                    <button
                        type="button"
                        className="close"
                        aria-label="Close"
                        onClick={toggle}
                        style={{
                            color: theme === "dark-mode" ? "#eaf4ff9a" : "#1f2937",
                            opacity: 1,
                            fontSize: "2.4rem",
                            textShadow: "none",
                            backgroundColor: "transparent",
                            border: "none",
                            boxShadow: "none",
                            padding: 0,
                            lineHeight: 1,
                            marginTop: "-0.8rem",
                        }}
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                )}
                style={{ padding: "0.4rem 0.8rem 0 1rem", margin: 0 }}
            >
                {title}
            </ModalHeader>

            <ModalBody
                className="d-flex flex-column"
                style={{
                    overflow: "hidden",
                    padding: "1rem 1rem",
                    flex: "1 1 auto",
                    minHeight: 0,
                }}
            >
                {rows.length > 0 && (
                    <div
                        className="d-flex flex-column flex-grow-1"
                        style={{ minHeight: 0 }}
                    >
                        <DataTableWindowed
                            id={"v1_import-preview-table"}
                            height="600px"
                            fillContainerHeight={true}
                            data={tableDataWithStableId}
                            columns={mappedColumns}
                            sortByColumn="__rowId"
                            sortDirection="ascending"
                            allowColumnCustomisation={true}
                            shrinkHeightToContents={false}
                            showGroupHeaders={true}
                            searchableFields={(mappedColumns || []).filter(x => !(["actions"].includes(x?.key))).map(c => c?.key)}
                            onSelectedDataChange={(arr) => {
                                setSelectedIds(new Set((arr || []).map(x => x.__rowId)));
                            }}
                            actions={[
                                <Button
                                    key="update"
                                    color={theme === "dark-mode" ? "dark" : "light"}
                                    outline
                                    disabled={selectedData.length === 0}
                                    onClick={() => {
                                        // Gather selected mapped rows in current view
                                        openBulkWithItems(selectedData);
                                    }}
                                >
                                    Update Selected
                                </Button>
                            ]}
                            allowMultiSelect={true}
                        />
                    </div>
                )}
            </ModalBody>

            <ModalFooter style={{ padding: "0.5rem 1rem 0.8rem" }}>
                <Button color={theme === "dark" ? "light" : "dark"} outline onClick={toggle}>
                    Close
                </Button>
                <Button
                    color="secondary"
                    // disabled={!rows.length || groupNames.length === 0 || mappedData.some(r => !r.groupName)}
                    disabled={!rows.length}
                    onClick={handleImport}
                    title={
                        groupNames.length === 0 
                            ? "Add at least one group name" 
                            : mappedData.some(r => !r.groupName)
                                ? "Please assign a group name to all rows"
                                : ""
                    }
                >
                    Import
                </Button>
            </ModalFooter>

            <Modal isOpen={isSearchOpen} centered fullscreen toggle={closeSearch} size="xl">
                <ModalHeader toggle={closeSearch}>Search Catalogue</ModalHeader>
                <ModalBody>
                    <ItemsTable mode="import" onAddItemToImport={handleAddItem} />
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={closeSearch}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>

            <QuoteItemBulkModal
                modalOpen={bulkModalOpen}
                setModalOpen={setBulkModalOpen}
                items={bulkItems}
                suppliers={suppliersData || []}
                itemTypes={itemTypes || []}
                groupNames={groupNames || []}
                overrideGroupNames={setGroupNames}
                isQuoteImport={true}
                onSave={handleBulkSave}
            />
        </div>
    );
};
export default LegacyQuotesImportModal;
